import React from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";
import _ from "lodash";

import { useAnalyticsApi } from "src/core/analytics/api/analytics.api";
import {
  ordersApi,
  CountOrdersResult
} from "src/core/orders/api/orders.api";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import { IStore } from "src/core/shared/store";
import { IApiError } from "src/library/utils";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { dashboardLayoutConfig } from "src/core/dashboard/contexts/DashboardContext";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  NumberWidget,
  NumberWidgetProps
} from "../NumberWidget";
import DataLoadError from "../DataLoadError";
import { getNumberFontSize } from "../utils";

// This will be used to map and create elements utilising NumberWidget for each element
type RealTimeData = RealTimeDataItem[];

interface RealTimeDataItem extends Pick<NumberWidgetProps, "displayData" | "units" | "valueType" | "fontSize" | "hideToolTip"> {
    /** unique identifier for the item  */
    id: string;
}

enum MetricId {
  NewOrders = "new-order-count",
  DeliveriesToday = "deliveries-today",
  AdminUsers = "admin-users",
  CustomerUsers = "customer-users",
  Branches = "branches",
  BulkBranches = "bulk-branches"
}

enum CacheKey {
  COUNT_NEW_ORDERS = "COUNT_ORDERS",
  COUNT_DELIVERIES_TODAY = "COUNT_DELIVERIES_TODAY",
  LIST_METRICS = "LIST_METRICS"
}

export const RealTimeDataWidget: React.FunctionComponent = () => {
  const { orderStatuses, installedModules } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    installedModules: (store.global.settings && store.global.settings.installedModules) || []
  })
  );

  const [ newOrdersCountRes, countNewOrdersReq ] = ordersApi<CountOrdersResult>("COUNT_ORDERS");
  const [ deliveriesTodayRes, countDeliveriesTodayReq ] = ordersApi<CountOrdersResult>("COUNT_ORDERS");
  const [ getMetricsResponse, getMetricsRequest ] = useAnalyticsApi("LIST_METRICS");
  const [ realTimeData, setRealTimeData ] = React.useState<RealTimeData>([]);
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const isLoading = deliveriesTodayRes.pending || newOrdersCountRes.pending || getMetricsResponse.pending;
  const errorMessage = deliveriesTodayRes.error?.message || newOrdersCountRes.error?.message || getMetricsResponse.error?.message;

  const fontSize = React.useMemo(() => {
    return getNumberFontSize(width, 5);
  }, [ width ]);

  const orderStatusIds = {
    submitted: getOrderStatusIdFromCode(orderStatuses, "submitted"),
    draft: getOrderStatusIdFromCode(orderStatuses, "draft"),
    cancelled: getOrderStatusIdFromCode(orderStatuses, "cancelled"),
    error: getOrderStatusIdFromCode(orderStatuses, "error")
  };

  // Helper to ensure order of data always the same
  const sortRealTimeData = React.useCallback((data: RealTimeData) => {
    const sortOrder = [
      MetricId.NewOrders,
      MetricId.DeliveriesToday,
      MetricId.AdminUsers,
      MetricId.CustomerUsers,
      MetricId.Branches,
      MetricId.BulkBranches
    ];

    return data.sort((a, b) => {
      const indexA = sortOrder.indexOf(a.id as MetricId);
      const indexB = sortOrder.indexOf(b.id as MetricId);

      return indexA - indexB;
    });
  }, []);

  // Grab order count data on mount
  React.useEffect(() => {
    countNewOrdersReq({
      filters: {
        orderStatusId: { $not: orderStatusIds.draft },
        createdAt: { $between: [ moment.utc().startOf("day").toDate(), moment.utc().endOf("day").toDate() ] }
      },
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => CacheKey.COUNT_NEW_ORDERS
      }
    });

    countDeliveriesTodayReq({
      filters: {
        orderStatusId: {
          $not: [
            orderStatusIds.draft,
            orderStatusIds.cancelled,
            orderStatusIds.error
          ]
        },
        orderDeliveryDate: { $between: [ moment.utc().startOf("day").toDate(), moment.utc().endOf("day").toDate() ] }
      },
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => CacheKey.COUNT_DELIVERIES_TODAY
      }
    });
  // countNewOrdersReq & countDeliveriesTodayReq from ordersApi,
  // uses old request structure which causes infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderStatusIds.cancelled,
    orderStatusIds.draft,
    orderStatusIds.error
  ]);

  // Grab metric data on mount
  React.useEffect(() => {
    getMetricsRequest(null, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => CacheKey.LIST_METRICS
      }
    });
  }, [ getMetricsRequest ]);

  const handleOrderCountResponse = React.useCallback((countResult: CountOrdersResult | null, error: IApiError | null, id: MetricId.NewOrders | MetricId.DeliveriesToday) => {
    if (error) {
      toast.error(error.message);
    }

    if (countResult) {
      setRealTimeData(prevRealTimeData => {
        // cleanse list of metrics from the data we're updating
        const metrics = _.filter(prevRealTimeData, item => item.id !== id);

        metrics.push({
          id,
          displayData: { value: countResult.count },
          units: id === MetricId.NewOrders ? "New Orders" : "Deliveries Today",
          valueType: "number",
          fontSize,
          hideToolTip: !!(countResult.count < 10000)
        });

        return sortRealTimeData(metrics);
      });
    }
  }, [ fontSize, sortRealTimeData ]);

  React.useEffect(() => {
    handleOrderCountResponse(newOrdersCountRes?.data, newOrdersCountRes?.error, MetricId.NewOrders);
  }, [ handleOrderCountResponse, newOrdersCountRes ]);

  React.useEffect(() => {
    handleOrderCountResponse(deliveriesTodayRes?.data, deliveriesTodayRes?.error, MetricId.DeliveriesToday);
  }, [ deliveriesTodayRes, handleOrderCountResponse ]);

  React.useEffect(() => {
    if (getMetricsResponse.error) {
      toast.error(getMetricsResponse.error.message);

      return;
    }

    // transform data to match RealTimeData shape
    if (getMetricsResponse.data) {
      const desiredMetrics = [
        MetricId.AdminUsers,
        MetricId.CustomerUsers,
        MetricId.Branches
      ] as string[];

      if (installedModules.includes("order-import")) {
        desiredMetrics.push(MetricId.BulkBranches);
      }

      const metrics = _.reduce(getMetricsResponse.data, (acc, metric) => {
        const result: RealTimeData = [ ...acc ];

        if (!desiredMetrics.includes(metric.type)) {
          return result;
        }

        result.push({
          id: metric.type,
          displayData: { value: metric.data?.[ 0 ]?.value || 0 },
          units: metric.name,
          valueType: "number",
          fontSize,
          hideToolTip: metric.data?.[ 0 ]?.value ? !!(metric.data?.[ 0 ]?.value < 10000) : true
        });

        return result;
      }, [] as RealTimeData);

      setRealTimeData(prevRealTimeData => {
        // remove existing metrics from real time data, and replace with updated data
        const updatedRealTimeData = _.filter(prevRealTimeData, item => !desiredMetrics.includes(item.id));

        // add new metrics to the end of the list
        updatedRealTimeData.push(...metrics);

        return sortRealTimeData(updatedRealTimeData);
      });
    }
  }, [
    getMetricsResponse,
    fontSize,
    installedModules,
    sortRealTimeData
  ]);

  return (
    <StyledWrapper
      ref={wrapperRef}
      rowHeight={dashboardLayoutConfig.rowHeight}
    >
      {isLoading && (
        <LoadingSpinner type="circular" />
      )}

      {realTimeData.length > 0 && !isLoading && !errorMessage && realTimeData.map(item => (
        <div
          key={item.id}
          className="metric-wrapper"
        >
          <NumberWidget
            widgetId={item.id}
            displayData={item.displayData}
            units={item.units}
            valueType={item.valueType}
            fontSize={item.fontSize}
            hideToolTip={item.hideToolTip}
          />
        </div>
      ))}

      {errorMessage && (
        <DataLoadError errorMessage={errorMessage} />
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div<{rowHeight?: number}>`
  ${({ theme }) => css`
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
    display: flex;
    justify-content: space-between;

    .metric-wrapper {
      flex: 1 1 0;
    }

    .value {
      &.xl,
      &.xxl {
        font-size: 40px;
      }
    }

    .units {
      &.md,
      &.lg {
        font-size: ${theme.fonts.sizes.h4};
      }

      &.xl,
      &.xxl {
        font-size: ${theme.fonts.sizes.h3};
      }
    }
    
    .metric-wrapper:not(:first-child) {
      height: 100%;
      border-left: 1px solid ${theme.colors.lightGrey};
      padding-left: ${theme.spacing.padding.sm};
    }
  `}
`;
